import { useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "ui/Button/Button";
import { DATE_PICKER_WIDTH_NUMBER, DatePicker } from "ui/DatePicker/DatePicker";
import { Modal } from "ui/Modal/Modal";

export type DatePickerModalProps = {
  onClickConfirm: (newDate: Date) => void;
  onClickCancel: () => void;
  isOpen: boolean;
  initialValue?: Date;
  minDate?: Date;
  maxDate?: Date;
};

export function DatePickerModal({
  onClickConfirm,
  onClickCancel,
  isOpen,
  initialValue,
  minDate,
  maxDate,
}: DatePickerModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (initialValue) return initialValue;
    const nextTwoDays = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
    const date = initialValue ?? nextTwoDays;
    if (minDate && date < minDate) return minDate;
    if (maxDate && date > maxDate) return maxDate;
    return date;
  });

  const intl = useIntl();

  return (
    <Modal
      trapFocus={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      onClose={() => {}}
    >
      <Modal.Content maxW={DATE_PICKER_WIDTH_NUMBER + 32}>
        <Modal.Body p="s200">
          <DatePicker
            dayLabel={intl.formatMessage({ id: "components.date-picker-modal.day" })}
            monthLabel={intl.formatMessage({ id: "components.date-picker-modal.month" })}
            yearLabel={intl.formatMessage({ id: "components.date-picker-modal.year" })}
            onChange={setSelectedDate}
            initialValue={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Modal.Body>
        <Modal.Footer paddingX="s200" paddingTop="0" flexDir="column">
          <Button
            onClick={() => onClickConfirm(selectedDate)}
            variant="primary"
            size="lg"
            isFullWidth
            data-testid="date-picker-modal-confirm-button"
          >
            <FormattedMessage id="components.date-picker-modal.adjust-date-button-label" />
          </Button>
          <Button mt="s100" onClick={onClickCancel} variant="secondary" size="lg" isFullWidth>
            <FormattedMessage id="components.date-picker-modal.cancel-button-label" />
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
